import { MapPin, Calendar, Briefcase, Edit2, Globe, Twitter, Linkedin, Instagram, Plus } from 'lucide-react';
import ProfileAvatar from './ProfileAvatar';
import { fmtMonthYear, socialUrl } from './format';

const SOCIALS = [
  { key: 'twitter', Icon: Twitter, cls: 'bg-sky-500/15 hover:bg-sky-500/25 text-sky-400' },
  { key: 'linkedin', Icon: Linkedin, cls: 'bg-blue-600/15 hover:bg-blue-600/25 text-blue-400' },
  { key: 'instagram', Icon: Instagram, cls: 'bg-pink-500/15 hover:bg-pink-500/25 text-pink-400' },
];

export const ProfileHeader = ({ profile, onEdit, onAvatarChanged }) => {
  const joined = fmtMonthYear(profile.member_since || profile.created_at);
  const links = SOCIALS.filter((s) => profile[s.key]);
  return (
    <div className="glow-card p-6 sm:p-8 rounded-2xl mb-6 relative overflow-hidden" data-testid="profile-header">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-teal-500/5 pointer-events-none" />
      <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
        <ProfileAvatar name={profile.name} hasAvatar={profile.has_avatar} version={profile.avatar_version} onChanged={onAvatarChanged} />
        <div className="flex-1 text-center md:text-left min-w-0">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-bold mb-2 break-words" data-testid="profile-name">{profile.name || 'Unnamed trader'}</h1>
              {profile.occupation && (
                <p className="text-gray-400 text-lg mb-2 flex items-center justify-center md:justify-start gap-2" data-testid="profile-occupation">
                  <Briefcase className="h-4 w-4" /> {profile.occupation}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-1 text-sm text-gray-400">
                {profile.country && (
                  <span className="flex items-center gap-1" data-testid="profile-location"><MapPin className="h-4 w-4" />{profile.country}</span>
                )}
                {joined && (
                  <span className="flex items-center gap-1" data-testid="profile-joined"><Calendar className="h-4 w-4" />Joined {joined}</span>
                )}
                {profile.website && (
                  <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-purple-400 hover:text-purple-300" data-testid="profile-website-link">
                    <Globe className="h-4 w-4" />{profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onEdit}
              data-testid="profile-edit-btn"
              className="self-center md:self-start px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Edit2 className="h-4 w-4" /> Edit Profile
            </button>
          </div>
          {profile.bio ? (
            <p className="text-gray-300 max-w-2xl mb-5 whitespace-pre-line" data-testid="profile-bio">{profile.bio}</p>
          ) : (
            <button type="button" onClick={onEdit} data-testid="profile-add-bio-btn" className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-purple-300 transition-colors">
              <Plus className="h-4 w-4" /> Add a short bio so others know your trading style
            </button>
          )}
          {links.length > 0 && (
            <div className="flex items-center justify-center md:justify-start gap-3">
              {links.map(({ key, Icon, cls }) => (
                <a key={key} href={socialUrl(key, profile[key])} target="_blank" rel="noopener noreferrer" title={profile[key]} data-testid={`profile-social-${key}`} className={`p-2 rounded-lg transition-colors ${cls}`}>
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
